import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, BehaviorSubject } from "rxjs";
import { AuthService } from "../services/auth.service";
import { RuntimeConfigService } from "../services/runtime-config.service";
import { environment } from "../../environment/environment";

// Interfaces for User Favorite Apps
export interface UserFavApp {
  appliId: string;
  name: string;
  category: string;
  subCategory: string;
  subCategoryId?: string;  // Added to support new API
  isFavorite: boolean;
  link: string;
  androidImageUrl: string;
  dateAdded: string;
}

export interface UserFavAppResponse {
  userid: number;
  apps: UserFavApp[];
}

@Injectable({
  providedIn: 'root'
})
export class UserFavAppService {
  // Dynamic API URL - prefers runtime config, falls back to environment
  private get apiUrl(): string {
    return this.configService?.apiUrl || environment.baseUrl;
  }
  
  // Reactive state management
  private userFavAppsSubject = new BehaviorSubject<UserFavApp[]>([]);
  public userFavApps$ = this.userFavAppsSubject.asObservable();

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private configService: RuntimeConfigService
  ) {
    // Don't automatically load favorites - only load when user is authenticated
    // This prevents errors when service is injected on public pages
  }

  // Load user's favorite apps
  loadUserFavApps(): void {
    this.authService.getUser().then(user => {
      // Handle both user.sub and user.attributes.sub formats
      const userId = user?.sub || user?.attributes?.sub;
      if (userId) {
        // Use the new favorites endpoint
        this.http.get<any>(`${this.apiUrl}/user/${userId}/favorites`, this.getHttpOptions())
          .subscribe({
            next: (response) => {
              // Transform new API format to old format
              const apps: UserFavApp[] = [];
              if (response.favorites) {
                Object.keys(response.favorites).forEach(subcategoryId => {
                  const subcategoryApps = response.favorites[subcategoryId];
                  subcategoryApps.forEach((app: any) => {
                    // Infer category from subcategory name if categoryName is empty
                    let categoryName = app.categoryName || '';
                    if (!categoryName && app.subCategoryName) {
                      categoryName = this.inferCategoryFromSubcategory(app.subCategoryName);
                    }
                    
                    apps.push({
                      appliId: app.appliId,
                      name: app.appName,
                      category: categoryName,
                      subCategory: app.subCategoryName || '',
                      subCategoryId: app.subCategory || subcategoryId,
                      isFavorite: true,
                      link: app.link || '',
                      androidImageUrl: app.androidImageUrl || '',
                      dateAdded: ''
                    });
                  });
                });
              }
              console.log('Favorites loaded and emitting:', apps.length, 'apps');
              this.userFavAppsSubject.next(apps);
            },
            error: (error) => {
              console.error('Error loading favorite apps:', error);
            }
          });
      }
    });
  }

  // Infer category from subcategory name using keywords
  private inferCategoryFromSubcategory(subcategoryName: string): string {
    const lower = subcategoryName.toLowerCase();
    
    // Social media patterns
    if (lower.includes('social') || lower.includes('chat') || lower.includes('message')) {
      return 'Social';
    }
    // Travel patterns
    if (lower.includes('travel') || lower.includes('hotel') || lower.includes('flight')) {
      return 'Travel';
    }
    // Food patterns
    if (lower.includes('food') || lower.includes('restaurant') || lower.includes('delivery')) {
      return 'Food';
    }
    // Shopping patterns
    if (lower.includes('shop') || lower.includes('buy') || lower.includes('store')) {
      return 'Shopping';
    }
    // Professional patterns  
    if (lower.includes('work') || lower.includes('professional') || lower.includes('business')) {
      return 'Professional';
    }
    
    return subcategoryName; // Use subcategory name as category if no match
  }

  // Get user's favorite apps
  getUserFavApps(): Observable<UserFavAppResponse> {
    return new Observable<UserFavAppResponse>(observer => {
      this.authService.getUser().then(user => {
        // Handle both user.sub and user.attributes.sub formats
        const userId = user?.sub || user?.attributes?.sub;
        if (userId) {
          // Use the new favorites endpoint
          this.http.get<any>(`${this.apiUrl}/user/${userId}/favorites`, this.getHttpOptions())
            .subscribe({
              next: (response) => {
                // Transform new API format {userId, favorites: {subcategoryId: [apps]}} 
                // to old format {userid, apps: []}
                const apps: UserFavApp[] = [];
                if (response.favorites) {
                  // Flatten all apps from all subcategories
                  Object.keys(response.favorites).forEach(subcategoryId => {
                    const subcategoryApps = response.favorites[subcategoryId];
                    subcategoryApps.forEach((app: any) => {
                      // Infer category from subcategory name if categoryName is empty
                      let categoryName = app.categoryName || '';
                      if (!categoryName && app.subCategoryName) {
                        categoryName = this.inferCategoryFromSubcategory(app.subCategoryName);
                      }
                      
                      apps.push({
                        appliId: app.appliId,
                        name: app.appName,
                        category: categoryName,
                        subCategory: app.subCategoryName || '',
                        subCategoryId: app.subCategory || subcategoryId,
                        isFavorite: true,
                        link: app.link || '',
                        androidImageUrl: app.androidImageUrl || '',
                        dateAdded: ''
                      });
                    });
                  });
                }
                
                const transformedResponse: UserFavAppResponse = {
                  userid: parseInt(response.userId) || 0,
                  apps: apps
                };
                
                console.log('getUserFavApps loaded:', apps.length, 'apps');
                this.userFavAppsSubject.next(apps);
                observer.next(transformedResponse);
                observer.complete();
              },
              error: (error) => {
                observer.error(error);
              }
            });
        } else {
          observer.error('User not authenticated');
        }
      }).catch(error => {
        observer.error(error);
      });
    });
  }

  // Add app to favorites
  addToFavorites(app: any): Observable<any> {
    return new Observable<any>(observer => {
      this.authService.getUser().then(user => {
        // Handle both user.sub and user.attributes.sub formats
        const userId = user?.sub || user?.attributes?.sub;
        if (userId) {
          const payload = {
            appIds: [app.appliId] // Use new API format with array
          };
          
          this.http.post<any>(`${this.apiUrl}/user_fav_app/${userId}`, payload, this.getHttpOptions())
            .subscribe({
              next: (response) => {
                // Refresh the list after adding
                this.loadUserFavApps();
                observer.next(response);
                observer.complete();
              },
              error: (error) => {
                observer.error(error);
              }
            });
        } else {
          observer.error('User not authenticated');
        }
      }).catch(error => {
        observer.error(error);
      });
    });
  }

  // Add multiple apps to favorites at once
  addMultipleAppsToFavorites(appIds: string[]): Observable<any> {
    return new Observable<any>(observer => {
      this.authService.getUser().then(user => {
        // Handle both user.sub and user.attributes.sub formats
        const userId = user?.sub || user?.attributes?.sub;
        if (userId) {
          const payload = {
            appIds: appIds
          };
          
          this.http.post<any>(`${this.apiUrl}/user_fav_app/${userId}`, payload, this.getHttpOptions())
            .subscribe({
              next: (response) => {
                this.loadUserFavApps();
                observer.next(response);
                observer.complete();
              },
              error: (error) => {
                console.error('API Error:', error);
                observer.error(error);
              }
            });
        } else {
          observer.error('User not authenticated');
        }
      }).catch(error => {
        console.error('Auth service error:', error);
        observer.error(error);
      });
    });
  }

  // Remove app from favorites
  removeFromFavorites(appliId: string, subCategoryId?: string): Observable<any> {
    return new Observable<any>(observer => {
      this.authService.getUser().then(user => {
        // Handle both user.sub and user.attributes.sub formats
        const userId = user?.sub || user?.attributes?.sub;
        if (userId) {
          // If subCategoryId is not provided, find it from current favorites
          if (!subCategoryId) {
            const currentApps = this.userFavAppsSubject.value;
            const app = currentApps.find(a => a.appliId === appliId);
            if (app && app.subCategoryId) {
              subCategoryId = app.subCategoryId;
            } else {
              observer.error('Cannot remove favorite: subcategory ID not available');
              return;
            }
          }
          
          this.http.delete<any>(`${this.apiUrl}/user/${userId}/favorites?subCategoryId=${subCategoryId}`, this.getHttpOptions())
            .subscribe({
              next: (response) => {
                // Refresh the list after removing
                this.loadUserFavApps();
                observer.next(response);
                observer.complete();
              },
              error: (error) => {
                observer.error(error);
              }
            });
        } else {
          observer.error('User not authenticated');
        }
      }).catch(error => {
        observer.error(error);
      });
    });
  }

  // Check if app is in favorites
  isAppInFavorites(appId: string): Observable<boolean> {
    return new Observable<boolean>(observer => {
      this.authService.getUser().then(user => {
        // Handle both user.sub and user.attributes.sub formats
        const userId = user?.sub || user?.attributes?.sub;
        if (userId) {
          this.http.get<any>(`${this.apiUrl}/user_fav_app/${userId}/${appId}`, this.getHttpOptions())
            .subscribe({
              next: (response) => {
                observer.next(response.isFavorite || false);
                observer.complete();
              },
              error: (error) => {
                observer.next(false);
                observer.complete();
              }
            });
        } else {
          observer.next(false);
          observer.complete();
        }
      }).catch(error => {
        observer.next(false);
        observer.complete();
      });
    });
  }

  // Get current list of favorite apps
  getCurrentFavoriteApps(): UserFavApp[] {
    return this.userFavAppsSubject.value;
  }

  // Get user favorites for category detail page
  getUserFavorites(userId: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Authorization': `Bearer ${this.authService.idToken}`,
        'Content-Type': 'application/json'
      })
    };

    return this.http.get<any>(
      `${this.apiUrl}/user/${userId}/favorites`, 
      httpOptions
    );
  }

  // Set favorite app for a subcategory
  setFavoriteApp(userId: string, favoriteData: any): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Authorization': `Bearer ${this.authService.idToken}`,
        'Content-Type': 'application/json'
      })
    };

    return this.http.post<any>(
      `${this.apiUrl}/user/${userId}/favorites`, 
      favoriteData,
      httpOptions
    );
  }

  // Set multiple favorite apps for a subcategory
  setMultipleFavoriteApps(userId: string, favoriteDataArray: any[]): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Authorization': `Bearer ${this.authService.idToken}`,
        'Content-Type': 'application/json'
      })
    };

    // Send array of favorites to backend
    return this.http.post<any>(
      `${this.apiUrl}/user/${userId}/favorites/bulk`, 
      { favorites: favoriteDataArray },
      httpOptions
    );
  }

  // Delete favorite app by subcategory (and optionally specific app)
  deleteFavoriteApp(userId: string, subCategoryId: string, appliId?: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Authorization': `Bearer ${this.authService.idToken}`,
        'Content-Type': 'application/json'
      })
    };

    // Build query parameters
    let queryParams = `subCategoryId=${subCategoryId}`;
    if (appliId) {
      queryParams += `&appliId=${appliId}`;
    }

    return this.http.delete<any>(
      `${this.apiUrl}/user/${userId}/favorites?${queryParams}`, 
      httpOptions
    );
  }

  // HTTP options with authentication
  private getHttpOptions() {
    return {
      headers: new HttpHeaders({
        'Authorization': `Bearer ${this.authService.idToken}`,
        'Content-Type': 'application/json'
      })
    };
  }
}
